import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccountInfo } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { AppConfigService } from '../core/app-config.service';

const POST_LOGIN_PATH_STORAGE_KEY = 'link-ecab.post-login-path';

type LoginOptions = {
  preserveExisting?: boolean;
};

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly authService = inject(MsalService);
  private readonly appConfig = inject(AppConfigService);
  private readonly activeAccountState = signal<AccountInfo | null>(null);
  private readonly resolvedState = signal(false);
  private initializeRequest: Promise<void> | null = null;

  readonly activeAccount = this.activeAccountState.asReadonly();
  readonly isResolved = this.resolvedState.asReadonly();
  readonly isAuthenticated = computed(() => this.activeAccountState() !== null);

  async initialize(): Promise<void> {
    if (this.resolvedState()) {
      return;
    }

    if (this.initializeRequest) {
      return this.initializeRequest;
    }

    this.initializeRequest = this.initializeInternal()
      .finally(() => {
        this.initializeRequest = null;
      });

    return this.initializeRequest;
  }

  async login(redirectStartPage: string, options?: LoginOptions): Promise<void> {
    this.storePostLoginPath(redirectStartPage, options?.preserveExisting ?? false);

    await firstValueFrom(this.authService.loginRedirect({
      scopes: [...this.appConfig.current.apiScopes],
      redirectStartPage,
    }));
  }

  logout(): void {
    void firstValueFrom(this.authService.logoutRedirect());
  }

  peekPostLoginPath(): string | null {
    return sessionStorage.getItem(POST_LOGIN_PATH_STORAGE_KEY);
  }

  consumePostLoginPath(): string | null {
    const path = this.peekPostLoginPath();

    if (path) {
      sessionStorage.removeItem(POST_LOGIN_PATH_STORAGE_KEY);
    }

    return path;
  }

  private async initializeInternal(): Promise<void> {
    try {
      const redirectResult = await firstValueFrom(this.authService.handleRedirectObservable());
      const account = redirectResult?.account
        ?? this.authService.instance.getActiveAccount()
        ?? this.authService.instance.getAllAccounts()[0]
        ?? null;

      if (account) {
        this.authService.instance.setActiveAccount(account);
      }

      this.activeAccountState.set(account);
    } catch (error) {
      this.activeAccountState.set(null);
      console.error('MSAL redirect handling failed', error);
    } finally {
      this.resolvedState.set(true);
    }
  }

  private storePostLoginPath(path: string, preserveExisting: boolean): void {
    if (preserveExisting && this.peekPostLoginPath()) {
      return;
    }

    sessionStorage.setItem(POST_LOGIN_PATH_STORAGE_KEY, path);
  }
}